import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceUnits() {
  const sectionStyle = {
    padding: "1rem",
    paddingLeft: "35px",
    borderRadius: "12px",
    marginBottom: "2rem",
  };

  const absoluteUnits = [
    ["cm", "centimeters"],
    ["mm", "millimeters"],
    ["in", "inches (1in = 96px = 2.54cm)"],
    ["px", "pixels (1px = 1/96th of 1in)"],
    ["pt", "points (1pt = 1/72 of 1in)"],
    ["pc", "picas (1pc = 12pt)"],
  ];

  const relativeUnits = [
    ["em", "Relative to the font-size of the element (2em = 2x font size)"],
    ["ex", "Relative to the x-height of the current font (rarely used)"],
    ["ch", "Relative to the width of the '0' (zero) character"],
    ["rem", "Relative to font-size of the root element"],
    ["vw", "Relative to 1% of the viewport width"],
    ["vh", "Relative to 1% of the viewport height"],
    ["vmin", "Relative to 1% of the smaller viewport dimension"],
    ["vmax", "Relative to 1% of the larger viewport dimension"],
    ["%", "Relative to the parent element"],
  ];

  return (
    <div className="tutorial-container">
      {/* Title & Intro */}
      <h1 className="tutorial-title">📏 CSS Units</h1>
      <p className="tutorial-intro">
        CSS has several units for expressing lengths. Many properties like{" "}
        <code>width</code>, <code>margin</code>, <code>padding</code>, and{" "}
        <code>font-size</code> take length values.
        <br />A length is a number followed by a unit, e.g., <code>
          10px
        </code>{" "}
        or <code>2em</code>.
        <br />
        <b>Note:</b> No whitespace is allowed between the number and unit. If
        the value is 0, the unit can be omitted. Some properties allow negative
        lengths.
      </p>

      {/* Key Points */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #fce7f3, #fbcfe8)",
          color: "#4a044e",
        }}
      >
        <h2>🔑 Key Points</h2>
        <ul>
          <li>
            CSS lengths can be <b>absolute</b> (fixed) or <b>relative</b>{" "}
            (scalable).
          </li>
          <li>
            Absolute lengths are not recommended on screens but useful for
            print.
          </li>
          <li>
            Relative lengths scale better across different devices and
            viewports.
          </li>
          <li>
            Viewport units (<code>vw</code>, <code>vh</code>) are relative to
            the browser window.
          </li>
        </ul>
      </div>

      {/* Absolute Length Units */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #e0f7fa, #b2ebf2)",
          color: "#006064",
          overflowX: "auto",
        }}
      >
        <h2>📐 Absolute Length Units</h2>
        <table
          className="tutorial-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#006064",
                color: "#ffffff",
                textAlign: "left",
              }}
            >
              <th style={{ padding: "12px" }}>Unit</th>
              <th style={{ padding: "12px" }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {absoluteUnits.map((unit, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#b2ebf2" : "#e0f7fa",
                  transition: "all 0.3s ease",
                  cursor: "default",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#80deea")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    index % 2 === 0 ? "#b2ebf2" : "#e0f7fa")
                }
              >
                <td style={{ padding: "12px", fontWeight: "bold" }}>
                  {unit[0]}
                </td>
                <td style={{ padding: "12px" }}>{unit[1]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Relative Length Units */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #fff4e5, #ffe0b2)",
          color: "#6a4c00",
          overflowX: "auto",
        }}
      >
        <h2>📏 Relative Length Units</h2>
        <table
          className="tutorial-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#6a4c00",
                color: "#ffffff",
                textAlign: "left",
              }}
            >
              <th style={{ padding: "12px" }}>Unit</th>
              <th style={{ padding: "12px" }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {relativeUnits.map((unit, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#ffe0b2" : "#fff4e5",
                  transition: "all 0.3s ease",
                  cursor: "default",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#ffcc80")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    index % 2 === 0 ? "#ffe0b2" : "#fff4e5")
                }
              >
                <td style={{ padding: "12px", fontWeight: "bold" }}>
                  {unit[0]}
                </td>
                <td style={{ padding: "12px" }}>{unit[1]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Example */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #dbeafe, #e0f2fe)",
          color: "#1e3a8a",
        }}
      >
        <h2>🧠 Example</h2>
        <CodeCopyBlock
          language="css"
          code={`h1 {
  font-size: 60px;
}

p {
  font-size: 25px;
  line-height: 50px;
}`}
        />
        <p>
          Here we use <code>px</code> units. Remember, 0 values can omit the
          unit, and negative lengths are allowed for some properties.
        </p>
      </div>
      {/* Browser Support */}
      <div
        style={{
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
          background: "linear-gradient(90deg, #dbeafe, #ede9fe)", // soft blue-violet background
          color: "#1e1b4b",
        }}
      >
        <h2>🌐 Browser Support</h2>
        <p style={{ color: "#312e81" }}>
          The numbers in the table specify the first browser version that fully
          supports each length unit.
        </p>

        <div style={{ overflowX: "auto", marginTop: "1rem" }}>
          <table
            className="tutorial-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#4338ca",
                  color: "#ffffff",
                  textAlign: "left",
                }}
              >
                <th style={{ padding: "12px" }}>Length Unit</th>
                <th
                  style={{
                    padding: "12px",

                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="https://cdn.jsdelivr.net/gh/alrra/browser-logos/src/chrome/chrome_64x64.png"
                    alt="Chrome"
                    width="20"
                    height="20"
                  />
                </th>
                <th
                  style={{
                    padding: "12px",

                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="https://cdn.jsdelivr.net/gh/alrra/browser-logos/src/edge/edge_64x64.png"
                    alt="Edge"
                    width="20"
                    height="20"
                  />
                </th>
                <th
                  style={{
                    padding: "12px",

                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="https://cdn.jsdelivr.net/gh/alrra/browser-logos/src/firefox/firefox_64x64.png"
                    alt="Firefox"
                    width="20"
                    height="20"
                  />
                </th>
                <th
                  style={{
                    padding: "12px",

                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="https://cdn.jsdelivr.net/gh/alrra/browser-logos/src/safari/safari_64x64.png"
                    alt="Safari"
                    width="20"
                    height="20"
                  />
                </th>
                <th
                  style={{
                    padding: "12px",

                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="https://cdn.jsdelivr.net/gh/alrra/browser-logos/src/opera/opera_64x64.png"
                    alt="Opera"
                    width="20"
                    height="20"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                [
                  "em, ex, %, px, cm, mm, in, pt, pc",
                  "1.0",
                  "3.0",
                  "1.0",
                  "1.0",
                  "3.5",
                ],
                ["ch", "27.0", "9.0", "1.0", "7.0", "20.0"],
                ["rem", "4.0", "9.0", "3.6", "4.1", "11.6"],
                ["vh, vw", "20.0", "9.0", "19.0", "6.0", "20.0"],
                ["vmin", "20.0", "12.0", "19.0", "6.0", "20.0"],
                ["vmax", "26.0", "16.0", "19.0", "7.0", "20.0"],
              ].map((row, index) => (
                <tr
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#e0e7ff" : "#c7d2fe",
                    transition: "background-color 0.3s ease",
                    cursor: "default",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#a5b4fc")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      index % 2 === 0 ? "#e0e7ff" : "#c7d2fe")
                  }
                >
                  {row.map((cell, i) => (
                    <td key={i} style={{ padding: "12px", color: "#1e1b4b" }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #fef9c3, #fef08a)",
          color: "#713f12",
        }}
      >
        <h2>🚀 Next Steps</h2>
        <ul>
          <li>
            Experiment with absolute and relative length units for scalable
            layouts.
          </li>
          <li>
            Try combining <code>em</code>, <code>rem</code>, and viewport units
            (<code>vw</code>, <code>vh</code>).
          </li>
          <li>
            Check browser support when using less common units like{" "}
            <code>ch</code> or <code>ex</code>.
          </li>
        </ul>
      </div>
    </div>
  );
}
